import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { m as motion, MAnimatePresence as AnimatePresence } from "../../../shared/components/Motion";
import { ButtonComponent } from "shared/components";
import { useAdmin } from "../AdminProvider";
import { modalVariants, childVariants, cld } from "shared/utils";
import "../styles/EditCertificatesModal.css";

const resolveImg = (val, width) => {
  if (!val) return "";
  if (val.startsWith("http")) return val;
  return cld(val, width);
};
const EditHomeModal = ({
  isOpen,
  onClose,
  onSave,
  homeData,
  publishedData,
}) => {
  const { homeDraft, saveHomeDraft } = useAdmin();
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    description: "",
    imageUrl: "",
    socialLinks: [],
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [, setImageFile] = useState(null);
  const [editingLinkIndex, setEditingLinkIndex] = useState(null);
  const [isAddingNewLink, setIsAddingNewLink] = useState(false);
  const [newLinkForm, setNewLinkForm] = useState({
    href: "",
    iconClass: "",
    title: "",
  });

  useEffect(() => {
    if (isOpen) {
      // Modal is opening - load current data (draft or published)
      const displayData = homeDraft || publishedData || {};
      setFormData({
        title: displayData.title || "",
        subtitle: displayData.subtitle || "",
        description: displayData.description || "",
        imageUrl: displayData.imageUrl || "",
        socialLinks: displayData.socialLinks || [],
      });
      setErrors({});
      setImageFile(null);
    } else {
      // Modal is closing - reset form
      setFormData({
        title: "",
        subtitle: "",
        description: "",
        imageUrl: "",
        socialLinks: [],
      });
      setErrors({});
      setImageFile(null);
      setEditingLinkIndex(null);
      setIsAddingNewLink(false);
      setNewLinkForm({
        href: "",
        iconClass: "",
        title: "",
      });
    }
  }, [isOpen, homeDraft, publishedData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }
    if (!formData.subtitle.trim()) {
      newErrors.subtitle = "Subtitle is required";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }
    if (!formData.imageUrl.trim()) {
      newErrors.imageUrl = "Image URL is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const updatedHome = {
        title: formData.title.trim(),
        subtitle: formData.subtitle.trim(),
        description: formData.description.trim(),
        imageUrl: formData.imageUrl.trim(),
        socialLinks: formData.socialLinks || [],
      };

      await saveHomeDraft(updatedHome);
      onSave();
      onClose();
    } catch (error) {
      console.error("Error saving home:", error);
      alert("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: null });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setFormData({ ...formData, imageUrl: previewUrl });
    }
  };

  const handleSocialChange = (index, field, value) => {
    const newLinks = [...formData.socialLinks];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setFormData({ ...formData, socialLinks: newLinks });
  };

  const removeSocialLink = (index) => {
    const newLinks = formData.socialLinks.filter((_, i) => i !== index);
    setFormData({ ...formData, socialLinks: newLinks });
  };

  const handleAddNewLinkClick = () => {
    setIsAddingNewLink(true);
    setEditingLinkIndex(null);
    setNewLinkForm({ href: "", iconClass: "", title: "" });
  };

  const handleSaveNewLink = () => {
    if (!newLinkForm.href.trim() || !newLinkForm.iconClass.trim()) {
      alert("Please fill in URL and Icon Class");
      return;
    }

    const newLinks = [...formData.socialLinks, { ...newLinkForm }];

    setFormData({ ...formData, socialLinks: newLinks });
    setIsAddingNewLink(false);
    setNewLinkForm({ href: "", iconClass: "", title: "" });
  };

  const handleCancelNewLink = () => {
    setIsAddingNewLink(false);
    setNewLinkForm({ href: "", iconClass: "", title: "" });
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="preview-modal active-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="preview-modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "900px",
              maxHeight: "90vh",
              overflow: "auto",
              width: "90%",
            }}
          >
            <i
              className="uil uil-times preview-modal-close"
              onClick={onClose}
            ></i>

            <motion.h3
              className="preview-modal-title"
              variants={childVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              Edit Home Section
            </motion.h3>

            <div className="preview-modal-container">
              {/* Left Side: Form Inputs */}
              <motion.div
                style={{ flex: 1 }}
                variants={childVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {/* Title Input */}
                <motion.div
                  style={{ position: "relative", marginBottom: "0.5rem" }}
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      marginBottom: "0.5rem",
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Title *
                  </label>
                  <input
                    className="input__element"
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., Santhosh S"
                    style={{
                      width: "100%",
                      height: "2.5rem",
                      padding: "0 1rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  {errors.title && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      {errors.title}
                    </span>
                  )}
                </motion.div>
                {/* Subtitle Input */}
                <motion.div
                  style={{ position: "relative", marginBottom: "0.5rem" }}
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      marginBottom: "0.5rem",
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Subtitle *
                  </label>
                  <input
                    className="input__element"
                    type="text"
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleInputChange}
                    placeholder="e.g., Software Engineer"
                    style={{
                      width: "100%",
                      height: "2.5rem",
                      padding: "0 1rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  {errors.subtitle && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      {errors.subtitle}
                    </span>
                  )}
                </motion.div>
                {/* Description Input */}
                <motion.div
                  style={{ position: "relative", marginBottom: "0.5rem" }}
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      marginBottom: "0.5rem",
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Description *
                  </label>
                  <textarea
                    className="input__element"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter your professional description..."
                    style={{
                      width: "100%",
                      minHeight: "4rem",
                      padding: "0.75rem 1rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                      boxSizing: "border-box",
                      fontFamily: "inherit",
                      resize: "vertical",
                      height: "90px",
                    }}
                  />
                  {errors.description && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      {errors.description}
                    </span>
                  )}
                </motion.div>
                {/* Social Links Section */}
                <motion.div
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      marginBottom: "1rem",
                      fontWeight: "600",
                      fontSize: "small",
                    }}
                  >
                    Social Links
                  </label>

                  {/* Social Links Cards Grid */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(160px, 1fr))",
                      gap: "0.75rem",
                    }}
                  >
                    {formData.socialLinks.map((link, index) => (
                      <motion.div
                        className="glass__card"
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        style={{
                          border: "2px solid var(--title-color)",
                          borderRadius: "0.5rem",
                          padding: "0.65rem",
                          position: "relative",
                          backgroundColor:
                            "color-mix(in srgb, var(--title-color) 15%, var(--body-color))",
                        }}
                      >
                        {/* Edit and Delete Icons */}
                        <div
                          style={{
                            position: "absolute",
                            top: "0.35rem",
                            right: "0.35rem",
                            display: "flex",
                            gap: "0.25rem",
                          }}
                        >
                          <motion.button
                            whileHover={{ scale: 1.15 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Show edit form for this link
                              setEditingLinkIndex(index);
                            }}
                            className="glass__button"
                            style={{
                              border: "none",
                              borderRadius: "50%",
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              color: "var(--body-color)",
                              padding: 0,
                              background: "var(--title-color)",
                            }}
                            title="Edit social link"
                          >
                            <i
                              className="bx bx-pencil"
                              style={{ fontSize: "14px" }}
                            ></i>
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.15 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSocialLink(index);
                            }}
                            className="glass__button"
                            style={{
                              border: "none",
                              borderRadius: "50%",
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              color: "var(--body-color)",
                              padding: 0,
                              background: "var(--title-color)",
                            }}
                            title="Delete social link"
                          >
                            <i
                              className="bx bx-trash"
                              style={{ fontSize: "14px" }}
                            ></i>
                          </motion.button>
                        </div>

                        {/* Social Link Content */}
                        <div style={{ paddingRight: "2.5rem" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              marginBottom: "0.5rem",
                            }}
                          >
                            <i
                              className={link.iconClass}
                              style={{
                                fontSize: "1.25rem",
                                color: "var(--title-color)",
                              }}
                            ></i>
                            <span
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                color: "var(--title-color)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {link.title || "Untitled"}
                            </span>
                          </div>
                          <p
                            style={{
                              fontSize: "0.65rem",
                              color: "var(--text-color)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {link.href || "No URL"}
                          </p>
                        </div>
                      </motion.div>
                    ))}

                    {/* Add New Social Link Card - as last element in grid */}
                    {!isAddingNewLink && editingLinkIndex === null && (
                      <motion.div
                        className="glass__card"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleAddNewLinkClick}
                        style={{
                          border: "var(--border-color)",
                          borderRadius: "0.5rem",
                          padding: "0.65rem",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: "65px",
                          cursor: "pointer",
                        }}
                      >
                        <i
                          className="bx bx-plus"
                          style={{
                            fontSize: "1.5rem",
                            color: "var(--title-color)",
                            marginBottom: "0.15rem",
                          }}
                        ></i>
                        <span
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: "500",
                            color: "var(--title-color)",
                          }}
                        >
                          Add Social Link
                        </span>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </motion.div>

              {/* Right Side: Image Preview and URL */}
              <motion.div
                className="image-upload-section"
                style={{
                  flex: "0.5 1 0%",
                  marginTop: "1.5rem",
                  gap: "1rem",
                  display: "flex",
                  flexDirection: "column",
                }}
                variants={childVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {/* Image Preview */}
                <label
                  className="glass__card"
                  style={{
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    width: "100%",
                    aspectRatio: "3 / 3",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor:
                      "color-mix(in srgb, var(--title-color) 15%, var(--body-color))",
                    position: "relative",
                    padding: "0.5rem",
                    border: "2px solid var(--title-color)",
                    cursor: "pointer",
                  }}
                >
                  {formData.imageUrl ? (
                    <>
                      <img
                        src={resolveImg(formData.imageUrl, 300)}
                        alt="Profile Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "0.5rem",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: "0.5rem",
                          right: "0.5rem",
                          width: "2.5rem",
                          height: "2.5rem",
                          borderRadius: "0.375rem",
                          backgroundColor: "var(--title-color)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        }}
                      >
                        <i
                          className="bx bx-upload"
                          style={{
                            fontSize: "1.5rem",
                            color: "var(--container-color)",
                          }}
                        ></i>
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--text-color)",
                        textAlign: "center",
                        padding: "1rem",
                      }}
                    >
                      <i
                        className="bx bx-upload"
                        style={{ fontSize: "2rem", marginBottom: "0.5rem" }}
                      ></i>
                      <span style={{ fontSize: "0.75rem" }}>
                        Upload Profile Image
                      </span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                </label>

                {/* Image URL Input */}
                <motion.div
                  style={{ position: "relative" }}
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <label
                    className="preview-modal-subtitle"
                    style={{
                      marginBottom: "0.5rem",
                      fontWeight: "600",
                      fontSize: "small",
                      marginTop: "1rem",
                    }}
                  >
                    Image URL *
                  </label>
                  <input
                    className="input__element"
                    type="url"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image.jpg"
                    style={{
                      width: "100%",
                      height: "2.5rem",
                      padding: "0 1rem",
                      border: "2px solid var(--title-color)",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      backgroundColor: "var(--body-color)",
                      color: "var(--border-color)",
                      outline: "none",
                    }}
                    onClick={() =>
                      formData.imageUrl &&
                      navigator.clipboard.writeText(formData.imageUrl)
                    }
                    title={
                      formData.imageUrl ? "Click to copy URL" : "Paste URL here"
                    }
                  />
                  {errors.imageUrl && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.7rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      {errors.imageUrl}
                    </span>
                  )}
                </motion.div>
              </motion.div>
            </div>
            {/* Edit/Add Form - Shows below grid */}
            {(editingLinkIndex !== null || isAddingNewLink) && (
              <>
                <motion.h5
                  className="preview-modal-subtitle"
                  style={{ marginTop: "1rem", fontSize: "0.95rem" }}
                  variants={childVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  Edit Social Link
                </motion.h5>
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: "1rem",
                      alignItems: "start",
                    }}
                  >
                    <div style={{ position: "relative", minHeight: "3.5rem" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                          color: "var(--title-color)",
                        }}
                      >
                        Tooltip Title *
                      </label>
                      <input
                        className="input__element"
                        type="text"
                        value={
                          editingLinkIndex !== null &&
                          formData.socialLinks &&
                          formData.socialLinks[editingLinkIndex]
                            ? formData.socialLinks[editingLinkIndex].title
                            : newLinkForm.title
                        }
                        onChange={(e) =>
                          editingLinkIndex !== null
                            ? handleSocialChange(
                                editingLinkIndex,
                                "title",
                                e.target.value,
                              )
                            : setNewLinkForm({
                                ...newLinkForm,
                                title: e.target.value,
                              })
                        }
                        placeholder="e.g., instagram-santhoshsivkumar"
                        style={{
                          width: "100%",
                          height: "2.5rem",
                          padding: "0 1rem",
                          border: "2px solid var(--title-color)",
                          borderRadius: "0.5rem",
                          fontSize: "0.875rem",
                          backgroundColor: "var(--body-color)",
                          color: "var(--border-color)",
                          outline: "none",
                        }}
                      />
                    </div>

                    <div style={{ position: "relative", minHeight: "3.5rem" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                          color: "var(--title-color)",
                        }}
                      >
                        Icon Class *
                      </label>
                      <input
                        className="input__element"
                        type="text"
                        value={
                          editingLinkIndex !== null &&
                          formData.socialLinks &&
                          formData.socialLinks[editingLinkIndex]
                            ? formData.socialLinks[editingLinkIndex].iconClass
                            : newLinkForm.iconClass
                        }
                        onChange={(e) =>
                          editingLinkIndex !== null
                            ? handleSocialChange(
                                editingLinkIndex,
                                "iconClass",
                                e.target.value,
                              )
                            : setNewLinkForm({
                                ...newLinkForm,
                                iconClass: e.target.value,
                              })
                        }
                        placeholder="e.g., bx bx-facebook"
                        style={{
                          width: "100%",
                          height: "2.5rem",
                          padding: "0 1rem",
                          border: "2px solid var(--title-color)",
                          borderRadius: "0.5rem",
                          fontSize: "0.875rem",
                          backgroundColor: "var(--body-color)",
                          color: "var(--border-color)",
                          outline: "none",
                        }}
                      />
                    </div>

                    <div
                      style={{
                        position: "relative",
                        minHeight: "3.5rem",
                        gridColumn: "1 / -1",
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                          color: "var(--title-color)",
                        }}
                      >
                        URL *
                      </label>
                      <input
                        className="input__element"
                        type="url"
                        value={
                          editingLinkIndex !== null &&
                          formData.socialLinks &&
                          formData.socialLinks[editingLinkIndex]
                            ? formData.socialLinks[editingLinkIndex].href
                            : newLinkForm.href
                        }
                        onChange={(e) =>
                          editingLinkIndex !== null
                            ? handleSocialChange(
                                editingLinkIndex,
                                "href",
                                e.target.value,
                              )
                            : setNewLinkForm({
                                ...newLinkForm,
                                href: e.target.value,
                              })
                        }
                        placeholder="https://example.com"
                        style={{
                          width: "100%",
                          height: "2.5rem",
                          padding: "0 1rem",
                          border: "2px solid var(--title-color)",
                          borderRadius: "0.5rem",
                          fontSize: "0.875rem",
                          backgroundColor: "var(--body-color)",
                          color: "var(--border-color)",
                          outline: "none",
                        }}
                      />
                    </div>
                  </div>

                  <motion.div
                    style={{
                      display: "flex",
                      gap: "0.75rem",
                      marginTop: "1rem",
                    }}
                    variants={childVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <ButtonComponent
                      buttonTitle="Cancel"
                      buttonText="Cancel"
                      onClick={
                        editingLinkIndex !== null
                          ? () => setEditingLinkIndex(null)
                          : handleCancelNewLink
                      }
                    />
                    <ButtonComponent
                      buttonTitle={editingLinkIndex !== null ? "Save" : "Add"}
                      buttonText={editingLinkIndex !== null ? "Save" : "Add"}
                      onClick={
                        editingLinkIndex !== null
                          ? () => setEditingLinkIndex(null)
                          : handleSaveNewLink
                      }
                    />
                  </motion.div>
                </motion.div>
              </>
            )}
            {/* Action Buttons */}
            <motion.div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
                justifyContent: "flex-end",
                marginTop: "1.5rem",
              }}
              variants={childVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <ButtonComponent
                buttonTitle="Cancel"
                buttonText="Cancel"
                onClick={onClose}
              />
              <ButtonComponent
                buttonTitle="Save Changes"
                buttonText={saving ? "Saving..." : "Save Changes"}
                onClick={handleSave}
                disabled={saving}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default EditHomeModal;
